/** @jsx jsx */
import { jsx } from '@emotion/core'
import style from './cartStyle';
import React from 'react';
//import { SiteLink } from '../../../components';
import env from '../../../env';
import { useGet, usePost } from '../../../components';
import { useForm } from '../../../form';
import { Loading, ErrorPopup } from '../../../components';
import { useStep } from './useStep';
import { StepNav } from '../cartStep/stepNav';
import { OrderItems } from '../cartOrderItems/orderItems';
import { getOrderItemFormData } from '../cartOrderItems/orderItemFormData'
import { Shipping } from '../cartShipping/shipping'
import { getShippingFormData } from '../cartShipping/shippingFormData';
import { Pay } from '../cartPayment/pay';
import utils from '../../../utils';
import { useStore } from '../../../store';

export function Cart() {
  const [cart, setCart] = React.useState(null);
  const [hasCartId, setHasCartId] = React.useState(true); // Assume true initially
  const get = useGet();

  // Reset body overflow when cart mounts (in case it was set to hidden by a modal on previous page)
  React.useEffect(() => {
    document.body.style.overflow = "unset";
  }, []);

  React.useEffect(() => {
    const cartId = env.getDevCartId();
    if (cartId && cartId !== 'null') {
      setHasCartId(true);
      get.send(env.apiBase + '/api/cart/getCart/' + cartId);
    } else {
      setHasCartId(false);
    }
  }, []);

  React.useEffect(() => {
    if (get.done() && get.response) {
      setCart(get.response);
    }
  }, [get.status]);

  // If there's no cartId, don't show loading - go straight to empty cart
  if (!hasCartId) {
    return <EmptyCart />
  }
  
  if (get.loading()) {
    return <Loading />
  } else if (get.hasErrors()) {
    return <ErrorPopup title="Loading Error" errors={get.errors} />
  }

  // Handle empty cart
  if (!cart || !cart.cartInfo || cart.cartInfo.totalQuantity === 0){
    return <EmptyCart/>
  }
  
  // Ensure cart has orderItems
  if (!cart.orderItems || cart.orderItems.length === 0) {
    return <EmptyCart/>
  }
  
  return <CartView cart={cart} setCart={setCart} />
}

function CartView({ cart, setCart }) {
  const [sendingStatus, setSendingStatus] = React.useState(null);
  const [goNext, setGoNext] = React.useState(false);
  const [action, setAction] = React.useState('save');
  const post = usePost();
  const stepInfo = useStep([
    { id: 'orderItems', title: 'Cart' },
    { id: 'shipping', title: 'Your Details' },
    { id: 'payment', title: 'Pay' }
  ])
  const { steps, currentStep, lastStep, moveNext, markAsFilled } = stepInfo;
  const { dispatch } = useStore();

  // Initialize forms with cart data
  const orderItemForm = useForm(getOrderItemFormData(cart), { onControlChanged: orderItemChanged });
  const shippingForm = useForm(getShippingFormData(cart), { onControlChanged: orderItemChanged });

  // Update form when cart changes - use cart.orderItems length as key to detect changes
  const cartItemsKey = cart?.orderItems?.length || 0;
  React.useEffect(() => {
    if (cart && cart.orderItems && cartItemsKey > 0) {
      const newModel = getOrderItemFormData(cart);
      orderItemForm.reset(newModel);
    }
    // eslint-disable-next-line
  }, [cartItemsKey]);

  function orderItemChanged(e, control) {
    if (control && control.name === 'quantity') {
      const itemsControl = orderItemForm.findControl('items');
      const formIndex = itemsControl.findIndexById(control.parent.id);
      
      // Find the corresponding orderItem by matching orderItemId
      // Filter to only orderItems (type 0) to match form structure
      const orderItemsOnly = cart.orderItems.filter(x => x.orderItemType === 0);
      const orderItem = orderItemsOnly[formIndex];
      
      if (!orderItem || !orderItem.orderItemId) {
        console.error('Cannot find order item for quantity update:', { formIndex, orderItemsOnly });
        return;
      }
      
      const qty = parseInt(control.value) || 0;
      if (qty < 1) {
        alert('Quantity must be at least 1');
        control.value = orderItem.quantity || 1;
        return;
      }
      
      const value = { 
        qty: qty, 
        orderItemId: orderItem.orderItemId, 
        cartId: env.getDevCartId() 
      };
      setAction('save');
      setSendingStatus('pending');
      setGoNext(false);
      post.send(env.apiBase + "/api/cart/updateQty", value);
    }
    else if(control && (control.name === 'shippingMethod' || control.name === 'shippingCountry')){
      shippingForm.findControl("useSameAddress").value = false;
      if(control.value === 'Standard'){
        shippingForm.findControl("useSameAddress").value = true;
      }
      const value = { ...shippingForm.getValue(), cartId: env.getDevCartId() };
      setAction('save');
      setSendingStatus('pending');
      setGoNext(false);
      post.send(env.apiBase + "/api/cart/saveShippingInfo", value);
    }
  }

  function remove(e, index) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!cart || !cart.orderItems || index < 0 || index >= cart.orderItems.length) {
      console.error('Invalid remove operation:', { cart, index, orderItemsLength: cart?.orderItems?.length });
      alert('Error: Cannot remove item. Please refresh the page and try again.');
      return;
    }
    
    const orderItem = cart.orderItems[index];
    if (!orderItem || !orderItem.orderItemId) {
      console.error('Invalid order item:', orderItem);
      alert('Error: Invalid item. Please refresh the page and try again.');
      return;
    }
    
    console.log('Removing item:', { orderItemId: orderItem.orderItemId, index, itemName: orderItem.itemName });
    
    const value = { orderItemId: orderItem.orderItemId, cartId: env.getDevCartId() };
    setAction('remove');
    setSendingStatus('pending');
    setGoNext(false);
    post.send(env.apiBase + "/api/cart/removeItem", value);
  }

  function saveShipping(e) {
    //const value = { ...shippingForm.getValue(), useSameAddress: true, cartId: env.getDevCartId() };
    const value = { ...shippingForm.getValue(), cartId: env.getDevCartId() };
    //console.log("valu", value);
    setAction('save');
    post.send(env.apiBase + "/api/cart/saveShippingInfo", value);
    setSendingStatus('pending');
    setGoNext(true);
  }
  
  React.useEffect(() => {
    if (post.done() && sendingStatus === 'pending') {
      const response = post.response;
      
      if (!response) {
        console.error('No response from server');
        setSendingStatus(null);
        return;
      }

      // Check for errors first
      if (response.errors && response.errors.length > 0) {
        console.error('API errors:', response.errors);
        setSendingStatus(null);
        alert('Error: ' + response.errors[0]);
        return;
      }

      if (!response.results) {
        console.error('No results in response:', response);
        setSendingStatus(null);
        return;
      }

      const newCart = response.results.cartModel;
      
      if (!newCart) {
        console.error('No cart model in response:', response);
        setSendingStatus(null);
        return;
      }

      setSendingStatus(null);
      
      // Update cart count from the response
      if (newCart.cartInfo) {
        const newCount = newCart.cartInfo.totalQuantity || 0;
        dispatch({type:'SET_STATE', payload : {cartInfo : newCount}});
        console.log('Cart count updated to:', newCount);
      }

      // Update cart state - this will trigger form reset via useEffect
      setCart(newCart);
      
      // Reset action after processing
      setAction('save');

      // Handle navigation
      if (goNext) {
        markAsFilled(currentStep);
        moveNext();
        setGoNext(false);
      }
    }
    
    // Reset sending status on error
    if (post.hasErrors() && sendingStatus === 'pending') {
      setSendingStatus(null);
      setAction('save');
    }
  }, [post.status, sendingStatus, action, goNext, currentStep, dispatch, markAsFilled, moveNext, post])

  
  const stepStyle = (index) => {
    const locStyle = index === currentStep ? style.stepCurrent : (
      index === currentStep - 1 ? style.stepPrev : (
        index === currentStep + 1 ? style.stepNext : style.step))
    const movingStyle = index === lastStep ? style.stepLast : null;
    return [locStyle, movingStyle];
  }

  const next = () => {
    markAsFilled(currentStep);
    moveNext();
    utils.ui.scrollTo('#ecmcart');
  }
  
  return <div css={style.cart} id="ecmcart">
    <div css={style.stepsWrapper}>
      <StepNav stepInfo={stepInfo} />
      <div css={style.steps} style={{ transform: `translateX(${-100 * currentStep}%)` }}>
        {steps.map((step, index) => {
          let stepView = null
          switch (step.id) {
            case 'orderItems': stepView = <OrderItems cart={cart}
              form={orderItemForm}
              moveNext={next}
              post={post}
              remove={remove} />; break;
            case 'shipping': stepView = <Shipping cart={cart}
              form={shippingForm}
              saveShipping={saveShipping}
              post={post} />; break;
            case 'payment': stepView = <Pay cart={cart}
              post={post} />; break;
            default: break;
          }
          return (
            <div css={stepStyle(index)} key={step.id}>
              {stepView}
            </div>
          )
        })}
      </div>
    </div>
  </div>;
}

function EmptyCart() {
  return <div css={style.cart}>
    <div css={style.stepsWrapper}>
      <h3>Your cart is empty.</h3>
    </div>
  </div>;
}